/**
 * Dev Gupta — Senior Software Engineer
 * World-class Portfolio with Three.js, real 3D model, system architecture
 * SEO-optimised · Performance-tuned · Dark ultra-premium aesthetic
 */

import { useEffect, useRef, useState, useCallback, memo } from "react";
import * as THREE from "three";
import { motion, AnimatePresence, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { VisitingCard } from "./VisitingCard";

/* ─── RESPONSIVE HOOK ─── */
function useWindowSize() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn, { passive: true });
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

/* ─── CONSTANTS ─── */
const G = "#C8A96E";
/* Body/label text sits on a near-black background, so any alpha meant to carry
   readable copy (>=0.15) is boosted — dark backdrop stays dark, text stays visible.
   Anything below 0.15 is decorative (hairline borders, background glow) and is untouched. */
const GA = (a) => `rgba(200,169,110,${a >= 0.15 ? Math.min(1, a * 1.5 + 0.15) : a})`;
const DARK = "#050607";
const DARKER = "#030404";

/* ─── DYNAMIC EXPERIENCE — recalculated from today, not hardcoded ───
   Rounds up to the year currently in progress (resume convention: "6+ years"
   means past year 5 and into year 6), so it reads correctly today and keeps
   advancing on its own each year without ever needing a manual edit. */
const NAV_ITEMS = [["hero", "Home"], ["skills", "Skills"], ["projects", "Projects"], ["architecture", "Architecture"], ["experience", "Experience"], ["card", "Card"], ["contact", "Contact"]];
const CAREER_START = new Date(2020, 11, 1); // Dec 2020 — first professional role (Appstech Solutions)
const EXPERIENCE_YEARS = Math.ceil((Date.now() - CAREER_START.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

/* ─── COMPANY DATA with SVG logos ─── */
const COMPANIES = [
  {
    id: "nityo",
    name: "Nityo Tech Pvt Ltd",
    full: "Nityo Infotech Services Pvt Ltd",
    role: "Senior Software Engineer",
    project: "AS Watson Group — Retail Tech Platform",
    period: "Feb 23, 2026 – Present",
    location: "Noida, UP",
    color: "#00A4E4",
    desc: "Leading full-stack & 3D development for AS Watson Group (world's largest health & beauty retailer — Watsons, Superdrug, Kruidvat). Building Angular/React components (RxJS, NgRx), AI agents, MCP server configs, LLM pipelines, Three.js/WebGL 3D visualisations and backend microservices using Python, Node.js, Java Spring Boot for global retail operations across 28 markets.",
    logo: "NITYO",
    logoColor: "#00A4E4",
    current: true,
  },
  {
    id: "astro",
    name: "Astro Arun Pandit",
    full: "Astro Arun Pandit Pvt Ltd",
    role: "Senior Full Stack Developer",
    project: "Astrology Platform Ecosystem",
    period: "Dec 2024 – Feb 20, 2026",
    location: "Noida, UP",
    color: "#FF6B35",
    desc: "Engineered Kundali Engine, Horocosmo Astrology Platform, Occult Gurukul with Three.js 3D Panchang. Led backend team, WebRTC integrations, GraphQL microservices and AWS deployments.",
    logo: "AAP",
    logoColor: "#FF6B35",
    current: false,
  },
  {
    id: "nordem",
    name: "Nordem Technology",
    full: "Nordem Technology Pvt Ltd",
    role: "Full Stack Developer",
    project: "Enterprise SaaS & POS Systems",
    period: "Oct 2023 – Nov 2024",
    location: "Jaipur, Rajasthan",
    color: "#7C9DBF",
    desc: "Built POS systems, e-bidding platforms and KYC onboarding using React, Angular, Node.js, Spring Boot with AWS CI/CD pipelines.",
    logo: "NTL",
    logoColor: "#7C9DBF",
    current: false,
  },
  {
    id: "immative",
    name: "Immative Technologies",
    full: "Immative Technologies Pvt Ltd",
    role: "Full Stack Developer",
    project: "3D Theatre & Supply Chain",
    period: "May 2021 – Oct 2023",
    location: "Mumbai, Maharashtra",
    color: "#8B7EC8",
    desc: "Built 3D Theatre (Three.js/WebGL), supply chain systems using PIXYZ CAD conversion, LMS platform and AWS-based REST APIs.",
    logo: "IMT",
    logoColor: "#8B7EC8",
    current: false,
  },
  {
    id: "appstech",
    name: "Appstech Solutions",
    full: "Appstech Solutions Pvt Ltd",
    role: "Full Stack Developer",
    project: "MERN Applications",
    period: "Dec 2020 – May 2021",
    location: "Ahmedabad, Gujarat",
    color: "#6BAE8E",
    desc: "Developed MERN full-stack applications including Process Management System and visitor management web app.",
    logo: "APS",
    logoColor: "#6BAE8E",
    current: false,
  },
];

/* Full merged skill set from all three résumé variants (Angular-focused, tailored
   full-stack, and AI/backend-focused) — every technology any of the three names,
   deduplicated and grouped by discipline rather than by which résumé said it. */
const SKILL_CATS = [
  { key: "Frontend", color: G },
  { key: "Backend", color: "#7C9DBF" },
  { key: "Data", color: "#6BAE8E" },
  { key: "Cloud & DevOps", color: "#8B7EC8" },
  { key: "AI & Agents", color: "#ea4b71" },
  { key: "Real-time & Testing", color: "#00A4E4" },
  { key: "Practices & Leadership", color: "#D97757" },
];
const SKILLS = [
  // Frontend
  { name: "React.js", cat: "Frontend" },
  { name: "Next.js", cat: "Frontend" },
  { name: "Angular (multi-version)", cat: "Frontend" },
  { name: "Vue.js", cat: "Frontend" },
  { name: "TypeScript", cat: "Frontend" },
  { name: "JavaScript (ES6+)", cat: "Frontend" },
  { name: "Three.js", cat: "Frontend" },
  { name: "WebGL / GLSL", cat: "Frontend" },
  { name: "RxJS / NgRx", cat: "Frontend" },
  { name: "Angular Material", cat: "Frontend" },
  { name: "Redux / State Mgmt", cat: "Frontend" },
  { name: "HTML5 / CSS3", cat: "Frontend" },
  { name: "PWA", cat: "Frontend" },
  { name: "Responsive / Cross-Browser UI", cat: "Frontend" },
  { name: "Bootstrap / jQuery", cat: "Frontend" },
  // Backend
  { name: "Node.js / Express", cat: "Backend" },
  { name: "Java / Spring Boot", cat: "Backend" },
  { name: "Hibernate", cat: "Backend" },
  { name: "Python / Flask / Django", cat: "Backend" },
  { name: "REST APIs", cat: "Backend" },
  { name: "GraphQL", cat: "Backend" },
  { name: "Microservices", cat: "Backend" },
  { name: "C / C++", cat: "Backend" },
  { name: "PHP", cat: "Backend" },
  // Data
  { name: "PostgreSQL", cat: "Data" },
  { name: "MongoDB", cat: "Data" },
  { name: "MySQL", cat: "Data" },
  { name: "NoSQL", cat: "Data" },
  { name: "Redis Caching", cat: "Data" },
  { name: "Database Indexing", cat: "Data" },
  { name: "ETL / Data Pipelines", cat: "Data" },
  // Cloud & DevOps
  { name: "AWS", cat: "Cloud & DevOps" },
  { name: "Docker", cat: "Cloud & DevOps" },
  { name: "Kubernetes", cat: "Cloud & DevOps" },
  { name: "Jenkins CI/CD", cat: "Cloud & DevOps" },
  { name: "Git", cat: "Cloud & DevOps" },
  { name: "CDN", cat: "Cloud & DevOps" },
  { name: "High Availability / Load Balancing", cat: "Cloud & DevOps" },
  // AI & Agents
  { name: "AI Agents", cat: "AI & Agents" },
  { name: "LLMs", cat: "AI & Agents" },
  { name: "Prompt Engineering", cat: "AI & Agents" },
  { name: "RAG", cat: "AI & Agents" },
  { name: "MCP (Model Context Protocol)", cat: "AI & Agents" },
  { name: "n8n Workflow Automation", cat: "AI & Agents" },
  { name: "PyTorch", cat: "AI & Agents" },
  { name: "TensorFlow", cat: "AI & Agents" },
  { name: "Vector Search & Recommendations", cat: "AI & Agents" },
  { name: "Model Evaluation & Optimization", cat: "AI & Agents" },
  // Real-time & Testing
  { name: "WebRTC", cat: "Real-time & Testing" },
  { name: "Socket.io", cat: "Real-time & Testing" },
  { name: "Jest", cat: "Real-time & Testing" },
  { name: "Mocha", cat: "Real-time & Testing" },
  { name: "Playwright", cat: "Real-time & Testing" },
  // Practices & Leadership
  { name: "Agile / Scrum", cat: "Practices & Leadership" },
  { name: "System Design & Design Patterns", cat: "Practices & Leadership" },
  { name: "Data Structures & Algorithms", cat: "Practices & Leadership" },
  { name: "Technical Leadership & Mentoring", cat: "Practices & Leadership" },
  { name: "Code Reviews & Architecture Planning", cat: "Practices & Leadership" },
];

const PROJECTS = [
  { title: "AS Watson — AI & 3D Platform", desc: "Built autonomous AI agents (n8n, MCP, LLM, RAG), Three.js/WebGL 3D retail visualisations and backend microservices using Python, Node.js, Java Spring Boot for AS Watson Group across 28 global markets.", tags: ["n8n","MCP","LLM","RAG","Three.js","Python","Node.js","Java","Spring Boot","Microservices"], color: "#00A4E4", year: "2026", cat: "AI/ML" },
  { title: "AI Agent Orchestration Framework", desc: "Reusable agent orchestration framework (n8n + Node.js MCP server) with RAG retrieval/ranking, structured prompt templates and guardrails for autonomous multi-step tool-calling across retail operations; containerised on Kubernetes/AWS.", tags: ["n8n","MCP","RAG","Node.js","Kubernetes","AWS"], color: "#ea4b71", year: "2026", cat: "AI/ML" },
  { title: "Kundali Engine", desc: "Vedic astrology computation — Panchang, Sodashvarga, Chalit & Transit logic in C/C++ integrated with Node.js + Java microservices at scale.", tags: ["Node.js","C/C++","Python","Java","PostgreSQL"], color: "#C8A96E", year: "2024", cat: "Platform" },
  { title: "Horocosmo Astrology Platform", desc: "WebRTC video calls, Socket.io real-time chat, GraphQL federation. Concurrent astrologer-client sessions with minimal latency.", tags: ["WebRTC","Socket.io","GraphQL","PostgreSQL","Redis"], color: "#7C9DBF", year: "2025", cat: "Astrology" },
  { title: "Occult Gurukul", desc: "Vue.js SPAs with Three.js 3D Panchang globe. Strapi CMS, multilingual i18n, Docker/AWS deployments at production scale.", tags: ["Vue.js","Three.js","Strapi","Docker","AWS"], color: "#8B7EC8", year: "2025", cat: "3D/WebGL" },
  { title: "3D Theatre Application", desc: "Interactive Three.js/WebGL theater with seat selection, camera projection, realistic audio, VR-ready architecture. Fully responsive.", tags: ["Three.js","WebGL","React.js","Node.js","MongoDB"], color: "#B8A06E", year: "2022", cat: "3D/WebGL" },
  { title: "AR/VR Heritage App (Amble)", desc: "Mumbai heritage sites with AR/VR, Mapbox API, AWS SNS, MERN stack. Immersive real-world experience through spatial computing.", tags: ["AR/VR","React.js","Mapbox","AWS SNS","MongoDB"], color: "#9D7CBF", year: "2021", cat: "AR/VR" },
  { title: "Enterprise POS System", desc: "Full POS for eCommerce. Vue.js + Spring Boot microservices + AWS Kubernetes. Multi-tenant, scalable with compliance guardrails.", tags: ["Vue.js","Spring Boot","K8s","PostgreSQL","AWS"], color: "#6BAE8E", year: "2024", cat: "Enterprise" },
  { title: "Maldives Customer Portal", desc: "Ferry & bus booking, live GPS tracking, wallet payments. Real-time data. Won project excellence recognition award.", tags: ["React.js","Node.js","MongoDB","Maps API"], color: "#7CAE9D", year: "2022", cat: "Platform" },
];

/* ─── CUSTOM CURSOR (Framer Motion springs) ─── */
function Cursor() {
  const mx = useMotionValue(-100), my = useMotionValue(-100);
  const ringX = useSpring(mx, { stiffness: 260, damping: 24, mass: 0.4 });
  const ringY = useSpring(my, { stiffness: 260, damping: 24, mass: 0.4 });
  useEffect(() => {
    const mv = (e) => { mx.set(e.clientX); my.set(e.clientY); };
    window.addEventListener("mousemove", mv, { passive: true });
    return () => window.removeEventListener("mousemove", mv);
  }, [mx, my]);
  return (
    <>
      <motion.div style={{ position: "fixed", left: mx, top: my, translateX: "-50%", translateY: "-50%", width: "5px", height: "5px", background: G, borderRadius: "50%", pointerEvents: "none", zIndex: 9999 }} />
      <motion.div style={{ position: "fixed", left: ringX, top: ringY, translateX: "-50%", translateY: "-50%", width: "28px", height: "28px", border: `1px solid ${GA(0.45)}`, borderRadius: "50%", pointerEvents: "none", zIndex: 9998 }} />
    </>
  );
}

/* ─── SCROLL PROGRESS ─── */
function ScrollBar() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const fn = () => { const t = document.documentElement.scrollHeight - window.innerHeight; setP(t > 0 ? (window.scrollY / t) * 100 : 0); };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "2px", background: "rgba(200,169,110,0.08)", zIndex: 200 }}>
      <div style={{ height: "100%", width: p + "%", background: `linear-gradient(90deg,${G},#8B7EC8)`, transition: "width 0.06s linear" }} />
    </div>
  );
}

/* ─── REVEAL ANIMATION (Framer Motion) ─── */
function Reveal({ children, delay = 0, style = {} }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.07, margin: "0px 0px -40px 0px" }}
      transition={{ duration: 0.75, delay, ease: [0.16, 1, 0.3, 1] }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── LIVE GITHUB STATS — real fetched data, not a hardcoded claim.
   Fails silently (returns null) on network error or rate-limit so it never
   blocks or breaks the page; the UI simply omits the line until it resolves. ─── */
function useGitHubStats(username) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`https://api.github.com/users/${username}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setStats({ repos: d.public_repos, followers: d.followers }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [username]);
  return stats;
}

/* ─── LAZY MOUNT — defers an expensive child (e.g. a WebGL scene) until it's
   about to scroll into view, instead of every 3D canvas initializing at once
   on page load. Reserves the given height up front so nothing jumps. ─── */
function LazyMount({ children, height }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { rootMargin: "200px 0px" });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} style={{ height }}>{visible && children}</div>;
}

/* ─── SECTION LABEL ─── */
const SLabel = ({ text }) => (
  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.32em", textTransform: "uppercase", color: G, marginBottom: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
    <div style={{ width: "36px", height: "1px", background: G, flexShrink: 0 }} />{text}
  </div>
);

const STitle = ({ children }) => (
  <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: "900", color: "#F5F5F7", lineHeight: "1.05", marginBottom: "52px" }}>{children}</h2>
);

/* ══════════════════════════════════════════════
   HERO CANVAS — Galaxy + Real GLB 3D Model
══════════════════════════════════════════════ */
function HeroCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const W = el.clientWidth, H = el.clientHeight;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x050607, 1);
    renderer.shadowMap.enabled = false;
    el.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050607);
    scene.fog = new THREE.FogExp2(0x050607, 0.04);
    const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 100);
    camera.position.set(0, 0, 4.5);

    /* ── Galaxy Particles ── */
    const N = 9000;
    const pos = new Float32Array(N * 3), col = new Float32Array(N * 3), sz = new Float32Array(N);
    // No white — only gold, blue, purple tones on black
    const pal = [new THREE.Color(G), new THREE.Color("#7C9DBF"), new THREE.Color("#8B7EC8"), new THREE.Color("#C8A96E")];
    for (let i = 0; i < N; i++) {
      const i3 = i * 3, r = Math.random() * 11 + 0.5, sp = r * 3, br = ((i % 3) / 3) * Math.PI * 2;
      const rr = (v) => (Math.pow(Math.random(), 3) * (Math.random() < 0.5 ? 1 : -1) * v);
      pos[i3] = Math.cos(br + sp) * r + rr(0.6);
      pos[i3 + 1] = rr(1.8);
      pos[i3 + 2] = Math.sin(br + sp) * r + rr(0.6);
      const c = pal[i % 4].clone().lerp(new THREE.Color("#C8A96E"), Math.random() * 0.15);
      col[i3] = c.r; col[i3 + 1] = c.g; col[i3 + 2] = c.b;
      sz[i] = Math.random() * 2.2 + 0.5;
    }
    const gGeo = new THREE.BufferGeometry();
    gGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    gGeo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    gGeo.setAttribute("size", new THREE.BufferAttribute(sz, 1));
    const gMat = new THREE.ShaderMaterial({
      vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: `attribute float size;varying vec3 vColor;void main(){vColor=color;vec4 mv=modelViewMatrix*vec4(position,1.0);gl_PointSize=min(size*(280.0/-mv.z),9.0);gl_Position=projectionMatrix*mv;}`,
      fragmentShader: `varying vec3 vColor;void main(){float d=distance(gl_PointCoord,vec2(0.5));if(d>0.5)discard;float a=1.0-smoothstep(0.0,0.5,d);gl_FragColor=vec4(vColor,a*0.82);}`,
    });
    const galaxy = new THREE.Points(gGeo, gMat);
    scene.add(galaxy);

    /* ── Hero character. The real asset is a user-supplied .glb (compressed
       from 101MB to 5.8MB via gltf-transform: texture resize to 1024px + WebP
       + meshopt geometry compression) at public/models/avatar.glb. Until it
       loads, a procedurally-built stand-in bust (spheres + primitives) shows
       immediately so the hero is never empty. Mouse-tracking below turns
       whichever is visible into a "character looking at the cursor". ── */
    const modelGroup = new THREE.Group();
    modelGroup.position.set(1.6, -0.55, 0);
    modelGroup.scale.setScalar(1.5);
    scene.add(modelGroup);
    let headBone = null; // set once the real model loads, if it has a "Head" bone

    // Not added to the scene up front — it only appears if the real model
    // below fails to load, so the hero never flashes a stand-in shape first.
    const placeholder = new THREE.Group();

    const skin = new THREE.MeshStandardMaterial({ color: "#e7c9ae", roughness: 0.55, metalness: 0.04 });
    const hairMat = new THREE.MeshStandardMaterial({ color: "#1c1a22", roughness: 0.35, metalness: 0.1 });
    const shirtMat = new THREE.MeshStandardMaterial({ color: "#f0eee8", roughness: 0.75 });
    const eyeMat = new THREE.MeshStandardMaterial({ color: "#241f2e", roughness: 0.15, metalness: 0.3 });
    const browMat = new THREE.MeshStandardMaterial({ color: "#161418", roughness: 0.5 });

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.5, 32, 32), skin);
    placeholder.add(head);

    [-0.48, 0.48].forEach((x) => {
      const ear = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 12), skin);
      ear.position.set(x, -0.02, 0);
      ear.scale.set(0.6, 1, 1);
      placeholder.add(ear);
    });

    const hairCap = new THREE.Mesh(new THREE.SphereGeometry(0.53, 32, 32, 0, Math.PI * 2, 0, Math.PI * 0.62), hairMat);
    hairCap.position.y = 0.08;
    hairCap.rotation.z = -0.12;
    placeholder.add(hairCap);
    const hairSide = new THREE.Mesh(new THREE.SphereGeometry(0.3, 16, 16), hairMat);
    hairSide.position.set(0.3, 0.24, 0.1);
    hairSide.scale.set(0.85, 0.7, 0.8);
    placeholder.add(hairSide);

    [-0.18, 0.18].forEach((x) => {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.065, 16, 16), eyeMat);
      eye.position.set(x, 0.02, 0.44);
      placeholder.add(eye);
      const brow = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.03, 0.02), browMat);
      brow.position.set(x, 0.15, 0.45);
      placeholder.add(brow);
    });

    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.09, 8), skin);
    nose.position.set(0, -0.08, 0.48);
    nose.rotation.x = Math.PI / 2.3;
    placeholder.add(nose);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.18, 0.22, 16), skin);
    neck.position.y = -0.58;
    placeholder.add(neck);
    const shoulders = new THREE.Mesh(new THREE.SphereGeometry(0.62, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2), shirtMat);
    shoulders.position.y = -0.78;
    shoulders.scale.set(1.1, 0.55, 0.85);
    placeholder.add(shoulders);

    (async () => {
      try {
        const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
          import("three/examples/jsm/loaders/GLTFLoader.js"),
          import("three/examples/jsm/libs/meshopt_decoder.module.js"),
        ]);
        if (MeshoptDecoder.ready) await MeshoptDecoder.ready;
        const loader = new GLTFLoader();
        loader.setMeshoptDecoder(MeshoptDecoder);
        const url = `${process.env.PUBLIC_URL}/models/avatar.glb`;
        loader.load(url, (gltf) => {
          const model = gltf.scene;
          // Auto-fit: measure the model's own bounding box and normalize it to a
          // known size/position, so it doesn't matter what units/pivot it was
          // exported with.
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const center = box.getCenter(new THREE.Vector3());
          const scale = 1.45 / (size.y || 1);
          model.scale.setScalar(scale);
          model.position.set(-center.x * scale, -center.y * scale - 0.15, -center.z * scale);
          model.traverse((child) => { if (child.isMesh) child.castShadow = true; });
          modelGroup.add(model);
          headBone = model.getObjectByName("Head") || model.getObjectByName("head") || null;
        }, undefined, (err) => {
          console.warn("Hero avatar failed to load — showing procedural fallback instead", err);
          modelGroup.add(placeholder);
        });
      } catch (err) {
        console.warn("GLTFLoader/MeshoptDecoder unavailable — showing procedural fallback instead", err);
        modelGroup.add(placeholder);
      }
    })();

    /* ── Floating code particles ── */
    const dpGeo = new THREE.BufferGeometry();
    const dpArr = new Float32Array(250 * 3);
    for (let i = 0; i < 250; i++) { dpArr[i * 3] = (Math.random() - 0.5) * 14; dpArr[i * 3 + 1] = (Math.random() - 0.5) * 9; dpArr[i * 3 + 2] = (Math.random() - 0.5) * 6 - 1; }
    dpGeo.setAttribute("position", new THREE.BufferAttribute(dpArr, 3));
    scene.add(new THREE.Points(dpGeo, new THREE.PointsMaterial({ color: G, size: 0.015, transparent: true, opacity: 0.15 })));

    /* ── Lights — warm key + cool fill + a front fill (so the character's actual
       colors read, not just a silhouette) + a violet rim glow behind it ── */
    scene.add(new THREE.AmbientLight("#5a5a66", 0.75));
    const pl1 = new THREE.PointLight(G, 1.8, 9); pl1.position.set(3, 3, 2); scene.add(pl1);
    const pl2 = new THREE.PointLight("#7C9DBF", 1.0, 7); pl2.position.set(-3, -1, 1); scene.add(pl2);
    const fill = new THREE.PointLight("#fff4e0", 5, 9);
    fill.position.set(modelGroup.position.x - 0.4, modelGroup.position.y + 0.8, modelGroup.position.z + 2.2);
    scene.add(fill);
    const fill2 = new THREE.PointLight("#fff4e0", 3, 9);
    fill2.position.set(modelGroup.position.x + 1, modelGroup.position.y + 0.4, modelGroup.position.z + 2);
    scene.add(fill2);
    const rim = new THREE.PointLight("#c85ee0", 3, 6);
    rim.position.set(modelGroup.position.x, modelGroup.position.y + 0.6, modelGroup.position.z - 1.6);
    scene.add(rim);
    const rimBaseIntensity = rim.intensity;

    /* ── Mouse parallax ── */
    const mouse = { x: 0, y: 0 };
    const onMv = (e) => { mouse.x = (e.clientX / window.innerWidth - 0.5) * 2; mouse.y = -(e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener("mousemove", onMv, { passive: true });

    let frame = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      frame++;
      const t = frame * 0.004;
      galaxy.rotation.y = t * 0.09;
      if (headBone) {
        // Real rig present: only the head turns to track the cursor (natural), the
        // body just sways gently — matches how a person actually looks at something.
        headBone.rotation.y += (mouse.x * 0.5 - headBone.rotation.y) * 0.08;
        headBone.rotation.x += (-mouse.y * 0.35 - headBone.rotation.x) * 0.08;
        modelGroup.rotation.y += (Math.sin(t * 0.3) * 0.08 - modelGroup.rotation.y) * 0.04;
      } else {
        // Procedural placeholder has no skeleton, so the whole bust turns instead.
        modelGroup.rotation.y += ((Math.sin(t * 0.3) * 0.15 + mouse.x * 0.6) - modelGroup.rotation.y) * 0.06;
        modelGroup.rotation.x += (mouse.y * -0.18 - modelGroup.rotation.x) * 0.06;
      }
      camera.position.x += (mouse.x * 0.4 - camera.position.x) * 0.035;
      camera.position.y += (mouse.y * 0.3 - camera.position.y) * 0.035;
      camera.lookAt(0, 0, 0);
      pl1.position.x = Math.sin(t) * 4;
      pl1.position.z = Math.cos(t) * 4;
      rim.intensity = rimBaseIntensity * (0.75 + Math.sin(t * 2.2) * 0.25);
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      if (!el) return;
      const w = el.clientWidth, h = el.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.removeEventListener("mousemove", onMv);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(animId);
      renderer.dispose();
      if (el && renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
  }, []);
  return <div ref={ref} style={{ position: "absolute", inset: 0 }} />;
}

/* ─── BLOOM — real, standard three.js post-processing (EffectComposer +
   UnrealBloomPass), not an experiment. Makes emissive nodes/orbs actually
   glow instead of reading as flat-shaded spheres — the concrete technique
   behind "realistic" for scenes built from data (a skills graph, an
   architecture diagram) that have no literal real-world object to fetch.
   Returns a render() function to call each frame in place of
   renderer.render(scene, camera); falls back to that automatically until
   the async import resolves, or forever if it fails. ─── */
function createBloomRenderer(renderer, scene, camera, W, H, strength = 0.55) {
  const state = { composer: null };
  (async () => {
    try {
      const [{ EffectComposer }, { RenderPass }, { UnrealBloomPass }] = await Promise.all([
        import("three/examples/jsm/postprocessing/EffectComposer.js"),
        import("three/examples/jsm/postprocessing/RenderPass.js"),
        import("three/examples/jsm/postprocessing/UnrealBloomPass.js"),
      ]);
      const composer = new EffectComposer(renderer);
      composer.addPass(new RenderPass(scene, camera));
      composer.addPass(new UnrealBloomPass(new THREE.Vector2(W, H), strength, 0.4, 0.15));
      state.composer = composer;
    } catch {
      // progressive enhancement only — plain renderer.render still runs every frame
    }
  })();
  return {
    render: () => (state.composer ? state.composer.render() : renderer.render(scene, camera)),
    setSize: (w, h) => state.composer?.setSize(w, h),
  };
}

/* ══════════════════════════════════════════════
   SKILLS SPHERE
══════════════════════════════════════════════ */
const SkillsSphere = memo(function SkillsSphere() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const W = el.clientWidth, H = el.clientHeight;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x050607, 1);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050607);
    const camera = new THREE.PerspectiveCamera(50, W / H, 0.1, 100);
    camera.position.z = 7.2;

    const grp = new THREE.Group();
    scene.add(grp);
    const nodes = [];
    const n = SKILLS.length;
    const catCol = Object.fromEntries(SKILL_CATS.map(c => [c.key, c.color]));
    SKILLS.forEach((sk, i) => {
      const phi = Math.acos(-1 + (2 * i) / n), theta = Math.sqrt(n * Math.PI) * phi, R = 3.3;
      const geo = new THREE.SphereGeometry(0.045 + (i % 3) * 0.01, 8, 8);
      const c = catCol[sk.cat];
      const mat = new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.45, metalness: 0.6, roughness: 0.3 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(R * Math.sin(phi) * Math.cos(theta), R * Math.cos(phi), R * Math.sin(phi) * Math.sin(theta));
      grp.add(mesh);
      nodes.push({ mesh, sk });
    });
    nodes.forEach(({ mesh: a }, i) => nodes.forEach(({ mesh: b }, j) => {
      if (j <= i) return;
      if (a.position.distanceTo(b.position) < 1.15) {
        const lg = new THREE.BufferGeometry().setFromPoints([a.position, b.position]);
        grp.add(new THREE.Line(lg, new THREE.LineBasicMaterial({ color: G, transparent: true, opacity: 0.07 })));
      }
    }));
    grp.add(new THREE.Mesh(new THREE.SphereGeometry(3.3, 28, 28), new THREE.MeshBasicMaterial({ color: G, wireframe: true, transparent: true, opacity: 0.04 })));
    scene.add(new THREE.AmbientLight("#fff", 0.45));
    const pl = new THREE.PointLight(G, 2, 10); pl.position.set(3, 3, 3); scene.add(pl);
    const bloomRenderer = createBloomRenderer(renderer, scene, camera, W, H, 0.5);

    const mouse = { x: 0, y: 0 };
    const mv = (e) => { const r = el.getBoundingClientRect(); mouse.x = ((e.clientX - r.left) / r.width - 0.5) * 2; mouse.y = -((e.clientY - r.top) / r.height - 0.5) * 2; };
    window.addEventListener("mousemove", mv, { passive: true });

    let t = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate); t += 0.004;
      grp.rotation.y = t * 0.25 + mouse.x * 0.28;
      grp.rotation.x = mouse.y * 0.15;
      nodes.forEach(({ mesh }, i) => mesh.scale.setScalar(1 + Math.sin(t * 2 + i * 0.6) * 0.06));
      pl.position.x = Math.sin(t) * 3; pl.position.z = Math.cos(t) * 3;
      bloomRenderer.render();
    };
    animate();
    return () => { window.removeEventListener("mousemove", mv); cancelAnimationFrame(animId); renderer.dispose(); if (el && renderer.domElement.parentNode === el) el.removeChild(renderer.domElement); };
  }, []);
  return <div ref={ref} style={{ width: "100%", height: "100%" }} />;
});

/* ══════════════════════════════════════════════
   SYSTEM ARCHITECTURE 3D SCENE
══════════════════════════════════════════════ */
const ArchScene = memo(function ArchScene() {
  const ref = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const NODES = [
    { id: "client", label: "Client Apps", sub: "React · Vue · Mobile", color: G, pos: [-4.5, 2, 0] },
    { id: "gateway", label: "API Gateway", sub: "Kong · Auth · Rate Limit", color: "#00A4E4", pos: [-2, 0, 0] },
    { id: "mcp", label: "MCP Server", sub: "Model Context Protocol", color: "#FF6B35", pos: [0, 1.5, 0] },
    { id: "n8n", label: "n8n Workflow", sub: "AI Agent Orchestration", color: "#ea4b71", pos: [0, -1.5, 0] },
    { id: "llm", label: "LLM / AI Layer", sub: "Prompt Eng. · PyTorch/TF Eval", color: "#8B7EC8", pos: [2.5, 1.5, 0] },
    { id: "rag", label: "RAG Pipeline", sub: "Vector DB · Embeddings", color: "#6BAE8E", pos: [2.5, -1.5, 0] },
    { id: "micro", label: "Microservices", sub: "Node · Python · Java", color: "#7C9DBF", pos: [4.5, 0.5, 0] },
    { id: "k8s", label: "Kubernetes", sub: "Docker · Auto-scale", color: "#326CE5", pos: [4.5, -0.8, 0] },
    { id: "db", label: "Data Layer", sub: "PostgreSQL · MongoDB · Redis", color: "#B8A06E", pos: [0, -3.5, 0] },
  ];
  const EDGES = [
    ["client", "gateway"], ["gateway", "mcp"], ["gateway", "n8n"],
    ["mcp", "llm"], ["n8n", "llm"], ["n8n", "rag"],
    ["llm", "micro"], ["rag", "micro"],
    ["micro", "k8s"], ["micro", "db"], ["k8s", "db"],
  ];

  useEffect(() => {
    const el = ref.current;
    const W = el.clientWidth, H = el.clientHeight;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x030404, 1);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x030404);
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 100);
    camera.position.set(0, 0, 10);

    const nodeMeshes = {};
    NODES.forEach(n => {
      const geo = new THREE.SphereGeometry(0.28, 16, 16);
      const mat = new THREE.MeshStandardMaterial({ color: n.color, emissive: n.color, emissiveIntensity: 0.5, metalness: 0.7, roughness: 0.2 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(...n.pos);
      mesh.userData = { id: n.id, label: n.label, sub: n.sub, color: n.color };
      scene.add(mesh);
      nodeMeshes[n.id] = mesh;
      // Ring around node
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.012, 6, 32), new THREE.MeshBasicMaterial({ color: n.color, transparent: true, opacity: 0.4 }));
      ring.rotation.x = Math.PI / 2;
      mesh.add(ring);
    });

    // Animated edges (dashed lines with pulse)
    EDGES.forEach(([a, b]) => {
      const pa = new THREE.Vector3(...NODES.find(n => n.id === a).pos);
      const pb = new THREE.Vector3(...NODES.find(n => n.id === b).pos);
      const pts = [pa, pb];
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({ color: GA(0.22), transparent: true, opacity: 0.4 });
      scene.add(new THREE.Line(geo, mat));
      // Pulse dot on edge
      const dot = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 6), new THREE.MeshBasicMaterial({ color: G, transparent: true, opacity: 0.8 }));
      dot.userData = { pa, pb, t: Math.random() };
      scene.add(dot);
    });

    scene.add(new THREE.AmbientLight("#fff", 0.35));
    const pl = new THREE.PointLight(G, 2, 14); pl.position.set(0, 3, 5); scene.add(pl);
    const bloomRenderer = createBloomRenderer(renderer, scene, camera, W, H, 0.6);

    // Raycaster for hover
    const raycaster = new THREE.Raycaster();
    const mPos = new THREE.Vector2();
    const mv = (e) => {
      const r = el.getBoundingClientRect();
      mPos.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      mPos.y = -((e.clientY - r.top) / r.height) * 2 + 1;
    };
    window.addEventListener("mousemove", mv, { passive: true });

    let t = 0, animId;
    const pulseDots = scene.children.filter(c => c.userData.pa);
    const animate = () => {
      animId = requestAnimationFrame(animate); t += 0.01;
      raycaster.setFromCamera(mPos, camera);
      const hits = raycaster.intersectObjects(Object.values(nodeMeshes));
      const hov = hits.length > 0 ? hits[0].object.userData : null;
      setTooltip(hov);
      Object.values(nodeMeshes).forEach(m => {
        const isHov = hov && m.userData.id === hov.id;
        m.material.emissiveIntensity += ((isHov ? 1.0 : 0.5) - m.material.emissiveIntensity) * 0.12;
        const ts = isHov ? 1.18 : 1;
        m.scale.x += (ts - m.scale.x) * 0.12;
        m.scale.y += (ts - m.scale.y) * 0.12;
        m.scale.z += (ts - m.scale.z) * 0.12;
        m.rotation.y = t * 0.4;
      });
      pulseDots.forEach(dot => {
        if (!dot.userData.pa) return;
        dot.userData.t = (dot.userData.t + 0.004) % 1;
        dot.position.lerpVectors(dot.userData.pa, dot.userData.pb, dot.userData.t);
        dot.material.opacity = 0.6 + Math.sin(t * 3 + dot.userData.t * Math.PI) * 0.35;
      });
      camera.position.x = Math.sin(t * 0.12) * 0.8;
      camera.position.y = Math.sin(t * 0.08) * 0.4;
      camera.lookAt(0, 0, 0);
      bloomRenderer.render();
    };
    animate();

    return () => { window.removeEventListener("mousemove", mv); cancelAnimationFrame(animId); renderer.dispose(); if (el && renderer.domElement.parentNode === el) el.removeChild(renderer.domElement); };
  }, []);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
      {tooltip && (
        <div style={{ position: "absolute", top: "16px", right: "16px", background: "rgba(5,6,7,0.94)", border: `1px solid ${tooltip.color}55`, padding: "16px 22px", backdropFilter: "blur(16px)", pointerEvents: "none", minWidth: "180px" }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "8px", letterSpacing: "0.2em", color: tooltip.color, textTransform: "uppercase", marginBottom: "5px" }}>Architecture Node</div>
          <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "16px", fontWeight: "700", color: "#EDEDF0", marginBottom: "4px" }}>{tooltip.label}</div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", color: GA(0.45) }}>{tooltip.sub}</div>
        </div>
      )}
    </div>
  );
});

/* ══════════════════════════════════════════════
   DNA HELIX (Experience)
══════════════════════════════════════════════ */
const HelixScene = memo(function HelixScene() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const W = el.clientWidth, H = el.clientHeight;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x030404, 1);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x030404);
    const camera = new THREE.PerspectiveCamera(55, W / H, 0.1, 100);
    camera.position.set(0, 0, 6);

    const grp = new THREE.Group();
    scene.add(grp);
    const pts1 = [], pts2 = [];
    for (let i = 0; i < 120; i++) {
      const t = (i / 120) * Math.PI * 7 - Math.PI * 3.5, R = 1.1;
      pts1.push(new THREE.Vector3(Math.cos(t) * R, t * 0.38, Math.sin(t) * R));
      pts2.push(new THREE.Vector3(Math.cos(t + Math.PI) * R, t * 0.38, Math.sin(t + Math.PI) * R));
      if (i % 12 === 0) {
        const p1 = new THREE.Vector3(Math.cos(t) * R, t * 0.38, Math.sin(t) * R);
        const p2 = new THREE.Vector3(Math.cos(t + Math.PI) * R, t * 0.38, Math.sin(t + Math.PI) * R);
        const idx = Math.floor(i / 12) % COMPANIES.length;
        const c = parseInt(COMPANIES[idx].color.replace("#", ""), 16);
        grp.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([p1, p2]), new THREE.LineBasicMaterial({ color: c, transparent: true, opacity: 0.65 })));
        [p1, p2].forEach(p => {
          const sm = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 8), new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.5, metalness: 0.7, roughness: 0.2 }));
          sm.position.copy(p); grp.add(sm);
        });
      }
    }
    [pts1, pts2].forEach((pts, ci) => {
      const curve = new THREE.CatmullRomCurve3(pts);
      grp.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 200, 0.022, 6, false), new THREE.MeshStandardMaterial({ color: ci === 0 ? G : "#7C9DBF", emissive: ci === 0 ? G : "#7C9DBF", emissiveIntensity: 0.15, metalness: 0.7, roughness: 0.3, transparent: true, opacity: 0.7 })));
    });

    scene.add(new THREE.AmbientLight("#fff", 0.4));
    const pl = new THREE.PointLight(G, 2, 10); pl.position.set(3, 0, 3); scene.add(pl);
    const bloomRenderer = createBloomRenderer(renderer, scene, camera, W, H, 0.45);

    let t = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate); t += 0.005;
      grp.rotation.y = t * 0.32;
      pl.position.x = Math.sin(t) * 3;
      bloomRenderer.render();
    };
    animate();
    return () => { cancelAnimationFrame(animId); renderer.dispose(); if (el && renderer.domElement.parentNode === el) el.removeChild(renderer.domElement); };
  }, []);
  return <div ref={ref} style={{ width: "100%", height: "100%" }} />;
});

/* ══════════════════════════════════════════════
   DASHED CORNER CARD — bracket-frame panel, minimal
══════════════════════════════════════════════ */
function DashedCard({ children, style = {}, accent = G, ...rest }) {
  const corner = (pos) => {
    const base = { position: "absolute", width: "10px", height: "10px", borderColor: accent, opacity: 0.85 };
    const map = {
      tl: { top: "-1px", left: "-1px", borderTop: `2px solid ${accent}`, borderLeft: `2px solid ${accent}` },
      tr: { top: "-1px", right: "-1px", borderTop: `2px solid ${accent}`, borderRight: `2px solid ${accent}` },
      bl: { bottom: "-1px", left: "-1px", borderBottom: `2px solid ${accent}`, borderLeft: `2px solid ${accent}` },
      br: { bottom: "-1px", right: "-1px", borderBottom: `2px solid ${accent}`, borderRight: `2px solid ${accent}` },
    };
    return { ...base, ...map[pos] };
  };
  return (
    <div style={{ position: "relative", border: `1px dashed ${GA(0.22)}`, background: "rgba(5,6,7,0.6)", backdropFilter: "blur(14px)", ...style }} {...rest}>
      {["tl", "tr", "bl", "br"].map(p => <div key={p} style={corner(p)} />)}
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════
   SOCIAL RAIL — fixed vertical icon strip, left edge (desktop only)
══════════════════════════════════════════════ */
function SocialRail() {
  const links = [
    { href: "https://github.com/Devgupta0", label: "GitHub", path: "M12 2C6.48 2 2 6.58 2 12.25c0 4.53 2.87 8.37 6.84 9.73.5.1.68-.22.68-.49 0-.24-.01-1.04-.01-1.89-2.78.62-3.37-1.21-3.37-1.21-.46-1.18-1.11-1.5-1.11-1.5-.9-.63.07-.62.07-.62 1 .07 1.53 1.05 1.53 1.05.89 1.56 2.34 1.11 2.91.85.09-.66.35-1.11.63-1.37-2.22-.26-4.56-1.14-4.56-5.07 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.31.1-2.72 0 0 .84-.28 2.75 1.05a9.24 9.24 0 0 1 5 0c1.91-1.33 2.75-1.05 2.75-1.05.55 1.41.2 2.46.1 2.72.64.72 1.03 1.63 1.03 2.75 0 3.94-2.34 4.8-4.57 5.06.36.32.68.94.68 1.9 0 1.37-.01 2.47-.01 2.81 0 .27.18.6.69.49A10.26 10.26 0 0 0 22 12.25C22 6.58 17.52 2 12 2Z" },
    { href: "https://www.linkedin.com/in/-devgupta", label: "LinkedIn", path: "M6.94 5a2 2 0 1 1-4-.02 2 2 0 0 1 4 .02ZM7 8.48H3V21h4V8.48Zm6.32 0H9.34V21h3.94v-6.57c0-3.66 4.77-3.96 4.77 0V21H22v-7.93c0-6.17-7.06-5.94-8.68-2.91V8.48Z" },
    { href: "mailto:devporwal00@gmail.com", label: "Email", path: "M2 5.5A1.5 1.5 0 0 1 3.5 4h17A1.5 1.5 0 0 1 22 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 18.5v-13Zm2.2.5 7.8 6.1L19.8 6H4.2ZM20 8.1l-7.4 5.8a1 1 0 0 1-1.2 0L4 8.1V18h16V8.1Z" },
  ];
  return (
    <div style={{ position: "fixed", left: "28px", bottom: "36px", zIndex: 90, display: "flex", flexDirection: "column", alignItems: "center", gap: "18px" }}>
      {links.map(l => (
        <a key={l.label} href={l.href} target="_blank" rel="noreferrer" aria-label={l.label} style={{ opacity: 0.55, transition: "opacity 0.25s, transform 0.25s" }}
          onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = 0.55; e.currentTarget.style.transform = ""; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill={G}><path d={l.path} /></svg>
        </a>
      ))}
      <div style={{ width: "1px", height: "64px", background: `linear-gradient(${GA(0.3)},transparent)` }} />
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN PORTFOLIO
══════════════════════════════════════════════ */
export default function Portfolio() {
  const w = useWindowSize();
  const isMobile = w < 768;
  const isTablet = w < 1024;

  const [scrolled, setScrolled] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [typeText, setTypeText] = useState("");
  const full = "Senior Software Engineer";

  /* Scroll-linked parallax on the hero copy — depth cue while the 3D canvas plays behind it */
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProgress, [0, 1], [0, 140]);
  const heroOpacity = useTransform(heroProgress, [0, 0.8], [1, 0]);

  const ghStats = useGitHubStats("Devgupta0");

  useEffect(() => {
    let i = 0;
    const iv = setInterval(() => { setTypeText(full.slice(0, i)); i++; if (i > full.length) clearInterval(iv); }, 65);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 55);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = useCallback((id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), []);

  return (
    <div style={{ fontFamily: "'Inter',sans-serif", background: DARK, color: "#E4E4E8", overflowX: "hidden", cursor: "none" }}>
      {/* SEO Meta (injected via Helmet equivalent inline) */}
      <title>Dev Gupta — Senior Software Engineer | AI · Full Stack · Three.js · Watson</title>

      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      <Cursor />
      <ScrollBar />
      {!isMobile && <SocialRail />}

      {/* ── STRUCTURED DATA (SEO) ── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Dev Gupta",
        "jobTitle": "Senior Software Engineer",
        "url": "https://devgupta.dev",
        "sameAs": ["https://www.linkedin.com/in/-devgupta", "https://github.com/Devgupta0"],
        "worksFor": { "@type": "Organization", "name": "Nityo Tech Pvt Ltd" },
        "knowsAbout": ["Three.js","React","Node.js","RAG","MCP","Microservices","WebRTC","AI Agents","n8n","Kubernetes"],
        "email": "devporwal00@gmail.com",
        "telephone": "+919582953281",
        "address": { "@type": "PostalAddress", "addressLocality": "Noida", "addressRegion": "UP", "addressCountry": "IN" }
      })}} />

      {/* ── NAV ── */}
      <nav style={{ position: "fixed", top: "2px", left: 0, right: 0, zIndex: 100, padding: "0 5vw", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between", background: scrolled ? "rgba(5,6,7,0.97)" : "rgba(5,6,7,0.7)", borderBottom: `1px solid ${GA(scrolled ? 0.12 : 0.06)}`, backdropFilter: "blur(24px)", transition: "all 0.4s" }}>
        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "21px", fontWeight: "900", color: G, cursor: "pointer", letterSpacing: "0.04em" }} onClick={() => go("hero")}>DG</div>
        {!isMobile && (
          <div style={{ display: "flex", gap: "32px", alignItems: "center" }}>
            {NAV_ITEMS.map(([id, label]) => (
              <span key={id} onClick={() => go(id)} style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.2em", textTransform: "uppercase", color: GA(0.5), cursor: "pointer", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = G} onMouseLeave={e => e.target.style.color = GA(0.5)}>{label}</span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          {!isMobile && (
            <a href="mailto:devporwal00@gmail.com" style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.18em", textTransform: "uppercase", color: G, border: `1px solid ${GA(0.3)}`, padding: "9px 20px", textDecoration: "none", cursor: "pointer", transition: "all 0.25s" }} onMouseEnter={e => { e.target.style.background = G; e.target.style.color = DARK; }} onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = G; }}>Let's Talk</a>
          )}
          {isMobile && (
            <button onClick={() => setNavOpen(o => !o)} style={{ background: "none", border: `1px solid ${GA(0.3)}`, padding: "8px 10px", cursor: "pointer", display: "flex", flexDirection: "column", gap: "4px" }}>
              {[0,1,2].map(i => <div key={i} style={{ width: "18px", height: "1.5px", background: G, transition: "all 0.3s", transform: navOpen && i === 0 ? "rotate(45deg) translate(4px,4px)" : navOpen && i === 2 ? "rotate(-45deg) translate(4px,-4px)" : "none", opacity: navOpen && i === 1 ? 0 : 1 }} />)}
            </button>
          )}
        </div>
      </nav>
      {/* Mobile nav drawer */}
      <AnimatePresence>
        {isMobile && navOpen && (
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            style={{ position: "fixed", top: "62px", left: 0, right: 0, zIndex: 99, background: "rgba(5,6,7,0.98)", borderBottom: `1px solid ${GA(0.1)}`, backdropFilter: "blur(24px)", padding: "16px 5vw 24px" }}>
            {NAV_ITEMS.map(([id, label]) => (
              <div key={id} onClick={() => { go(id); setNavOpen(false); }} style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", color: GA(0.6), padding: "13px 0", borderBottom: `1px solid ${GA(0.06)}`, cursor: "pointer" }}>{label}</div>
            ))}
            <a href="mailto:devporwal00@gmail.com" style={{ display: "inline-block", marginTop: "16px", fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase", color: G, border: `1px solid ${GA(0.3)}`, padding: "10px 22px", textDecoration: "none" }}>Let's Talk</a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════ HERO ══════════ */}
      <section id="hero" ref={heroRef} style={{ position: "relative", height: "100vh", overflow: "hidden", background: "#050607" }}>
        <HeroCanvas />
        {/* Dark only on the left where text lives — the right side (3D model + role cards) stays clear so the model is actually visible, not smothered under a flat veil */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(100deg, rgba(5,6,7,0.94) 0%, rgba(5,6,7,0.72) 32%, rgba(5,6,7,0.22) 55%, rgba(5,6,7,0.04) 78%)", pointerEvents: "none" }} />
        {/* Top bar */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "120px", background: "linear-gradient(rgba(5,6,7,0.95), transparent)", pointerEvents: "none" }} />
        {/* Bottom fade */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "200px", background: "linear-gradient(transparent, #050607)", pointerEvents: "none" }} />

        {/* Hero copy */}
        <motion.div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", padding: isMobile ? "0 6vw" : "0 8vw", pointerEvents: "none", maxWidth: isMobile ? "100%" : "700px", y: heroY, opacity: heroOpacity }}>
          <div style={{ opacity: 0, animation: "fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.3s forwards" }}>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.3em", textTransform: "uppercase", color: G, marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ width: "44px", height: "1px", background: G }} />
              Full Stack · AI · 3D Engineering
            </div>
          </div>
          <div style={{ opacity: 0, animation: "fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.48s forwards" }}>
            <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(62px,10vw,120px)", fontWeight: "900", lineHeight: "0.92", color: "#F5F5F7", letterSpacing: "-0.03em" }}>
              Dev<br />
              <span style={{ WebkitTextStroke: `2px ${G}`, color: "transparent" }}>Gupta</span>
            </h1>
          </div>
          <div style={{ opacity: 0, animation: "fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.66s forwards", marginTop: "18px" }}>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "clamp(11px,1.5vw,16px)", color: G, letterSpacing: "0.04em" }}>
              <span style={{ color: GA(0.45) }}>const role = "</span>
              {typeText}
              <span style={{ animation: "blink 1s step-end infinite", color: G }}>|</span>
              <span style={{ color: GA(0.45) }}>"</span>
            </div>
          </div>
          <div style={{ opacity: 0, animation: "fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.85s forwards", marginTop: "20px" }}>
            <p style={{ fontFamily: "'Inter',sans-serif", fontSize: isMobile ? "11px" : "13px", lineHeight: "1.95", color: "rgba(228,228,232,0.85)", textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
              {isMobile ? "AI · 3D · Full Stack · Microservices" : "AS Watson · AI Agents · Three.js/WebGL 3D · Node.js · Python · Java Spring Boot · Microservices"}<br />
              {EXPERIENCE_YEARS}+ Years · 25+ Products · Global Clients
            </p>
          </div>
          <div style={{ opacity: 0, animation: "fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) 1.05s forwards", marginTop: "32px", display: "flex", gap: "14px", pointerEvents: "all" }}>
            <button onClick={() => go("projects")} style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", padding: "14px 32px", background: G, color: "#050607", border: "none", cursor: "none", fontWeight: "700", transition: "all 0.3s" }}
              onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = `0 14px 36px ${GA(0.38)}`; }}
              onMouseLeave={e => { e.target.style.transform = ""; e.target.style.boxShadow = ""; }}>
              Explore Work
            </button>
            <a href="https://www.linkedin.com/in/-devgupta" target="_blank" rel="noreferrer" style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", padding: "14px 32px", background: "transparent", color: G, border: `1px solid ${GA(0.4)}`, textDecoration: "none", cursor: "none", transition: "all 0.3s", display: "inline-flex", alignItems: "center" }}
              onMouseEnter={e => { e.target.style.background = GA(0.08); e.target.style.borderColor = G; }}
              onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.borderColor = GA(0.4); }}>
              LinkedIn
            </a>
          </div>
        </motion.div>

        {/* Role highlight cards — desktop only, right side of hero */}
        {!isMobile && (
          <div style={{ position: "absolute", right: "6vw", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: "18px", width: "280px", zIndex: 2 }}>
            {[
              { title: "AI AGENT ENGINEER", sub: "Autonomous systems & LLM pipelines", body: "Building AI agents, RAG pipelines and MCP servers with n8n orchestration and prompt engineering.", delay: 1.2 },
              { title: "FULL-STACK DEVELOPER", sub: "Modern web & 3D applications", body: "Shipping React/Three.js frontends and Node.js/Java Spring Boot backends across cloud-native infrastructure.", delay: 1.4 },
            ].map((c) => (
              <motion.div key={c.title}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: c.delay, ease: [0.16, 1, 0.3, 1] }}>
                <DashedCard style={{ padding: "20px 22px" }}>
                  <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "16px", fontWeight: "700", color: "#F5F5F7", marginBottom: "4px" }}>{c.title}</div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", color: G, marginBottom: "10px", letterSpacing: "0.02em" }}>{c.sub}</div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", lineHeight: "1.7", color: GA(0.55) }}>{c.body}</div>
                </DashedCard>
              </motion.div>
            ))}
          </div>
        )}

        {/* Scroll indicator */}
        <div style={{ position: "absolute", bottom: "88px", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "7px", opacity: 0, animation: "fadeUp 1s ease 1.7s forwards" }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "8px", letterSpacing: "0.28em", textTransform: "uppercase", color: GA(0.35) }}>scroll</div>
          <div style={{ width: "1px", height: "42px", background: `linear-gradient(${G},transparent)`, animation: "scrollAnim 2s ease infinite" }} />
        </div>

        {/* Stats bar */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", borderTop: `1px solid ${GA(0.1)}`, background: "rgba(5,6,7,0.92)", backdropFilter: "blur(20px)" }}>
          {[[`${EXPERIENCE_YEARS}+`, "Years Experience"], ["25+", "Products Shipped"], ["20+", "Technologies"], ["5", "Companies"]].map(([n, l]) => (
            <div key={l} style={{ padding: isMobile ? "14px 0" : "18px 0", textAlign: "center", borderRight: `1px solid ${GA(0.08)}`, borderBottom: isMobile ? `1px solid ${GA(0.06)}` : "none" }}>
              <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: isMobile ? "22px" : "26px", fontWeight: "700", color: G }}>{n}</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "8px", letterSpacing: "0.2em", textTransform: "uppercase", color: GA(0.4), marginTop: "3px" }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════ ABOUT ══════════ */}
      <section style={{ padding: isMobile ? "64px 6vw" : "100px 8vw", position: "relative", overflow: "hidden", background: DARK }}>
        <div style={{ position: "absolute", top: "-100px", right: "-100px", width: "440px", height: "440px", borderRadius: "50%", background: `radial-gradient(circle, ${GA(0.045)} 0%, transparent 70%)`, pointerEvents: "none" }} />
        <Reveal>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr", gap: isMobile ? "40px" : "72px", alignItems: "center" }}>
            <div>
              <SLabel text="about.me" />
              <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(32px,4vw,52px)", fontWeight: "900", color: "#F5F5F7", lineHeight: "1.05", marginBottom: "28px" }}>
                Engineering at the<br /><span style={{ color: G, fontStyle: "italic" }}>intersection</span> of<br />AI and Scale
              </h2>
              <p style={{ fontFamily: "'Inter',sans-serif", fontSize: "12.5px", lineHeight: "2", color: GA(0.5), marginBottom: "18px" }}>
                Senior Software Engineer at <span style={{ color: "#00A4E4" }}>Nityo Tech Pvt Ltd</span>, working on <span style={{ color: G }}>AS Watson Group</span> retail tech platform for global markets. MCA from Amity University (GPA 7.8). {EXPERIENCE_YEARS}+ years across 5 companies.
              </p>
              <p style={{ fontFamily: "'Inter',sans-serif", fontSize: "12.5px", lineHeight: "2", color: GA(0.4) }}>
                Specialised in AI agent architectures, real-time platforms, 3D/WebGL visualisations, and cloud-native microservices at production scale.
              </p>
              <div style={{ marginTop: "30px", display: "flex", flexDirection: "column", gap: "7px" }}>
                {[["📍", "Noida, UP, India"], ["📧", "devporwal00@gmail.com"], ["📞", "+91 9582953281"], ["🔗", "linkedin.com/in/-devgupta"], ["💻", "github.com/Devgupta0"], ...(ghStats ? [["📊", `${ghStats.repos} public repos · ${ghStats.followers} followers (live)`]] : [])].map(([ic, v]) => (
                  <div key={v} style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", color: GA(0.42), letterSpacing: "0.03em" }}>{ic} {v}</div>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "12px" }}>
              {[["AI Agents · LLM · RAG", "AI Engineering"], ["React · Vue · Three.js", "Frontend"], ["Node · Python · Java", "Backend"], ["AWS · K8s · Docker", "DevOps"]].map(([t, s], i) => (
                <Reveal key={t} delay={i * 0.1}>
                  <DashedCard style={{ padding: "26px 20px", transition: "transform 0.3s" }}>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "13px", fontWeight: "700", color: "#EDEDF0", marginBottom: "5px" }}>{t}</div>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: G }}>{s}</div>
                  </DashedCard>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══════════ SKILLS ══════════ */}
      <section id="skills" style={{ padding: "0 0 80px", background: `linear-gradient(180deg, rgba(5,6,7,0.5) 0%, ${DARK} 100%)`, borderTop: `1px solid ${GA(0.06)}` }}>
        <div style={{ padding: isMobile ? "50px 6vw 32px" : "70px 8vw 44px" }}>
          <Reveal>
            <SLabel text="skills.map(s => s.master())" />
            <STitle>Technical <span style={{ color: G, fontStyle: "italic" }}>Arsenal</span></STitle>
          </Reveal>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", alignItems: "center", padding: isMobile ? "0 6vw 50px" : "0 8vw 60px" }}>
          {!isMobile && <LazyMount height="480px"><SkillsSphere /></LazyMount>}
          <div style={{ padding: isMobile ? "0" : "0 0 0 8vw" }}>
            <Reveal>
              <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(48px,6vw,84px)", fontWeight: "700", color: "#F5F5F7", lineHeight: "1" }}>{SKILLS.length}<span style={{ color: G }}>+</span></div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", color: GA(0.5), marginTop: "8px" }}>Technologies across {SKILL_CATS.length} disciplines</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "12.5px", lineHeight: "1.9", color: GA(0.45), marginTop: "18px", maxWidth: "380px" }}>
                Angular/RxJS depth, React/Next.js full-stack work, and AI agent engineering (RAG, MCP, n8n). Every node in the sphere is one real, résumé-verified skill.
              </div>
            </Reveal>
          </div>
        </div>

        <div style={{ padding: isMobile ? "0 6vw" : "0 8vw", display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(3,1fr)", gap: "14px" }}>
          {SKILL_CATS.map((cat, ci) => (
            <Reveal key={cat.key} delay={ci * 0.08}>
              <DashedCard accent={cat.color} style={{ padding: "22px 24px", height: "100%" }}>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "13px", fontWeight: "700", color: cat.color, letterSpacing: "0.02em", marginBottom: "14px" }}>{cat.key}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                  {SKILLS.filter(s => s.cat === cat.key).map(sk => (
                    <span key={sk.name} style={{ fontFamily: "'Inter',sans-serif", fontSize: "10.5px", color: GA(0.62), border: `1px solid ${cat.color}38`, background: `${cat.color}0c`, padding: "5px 10px", borderRadius: "999px", letterSpacing: "0.01em" }}>{sk.name}</span>
                  ))}
                </div>
              </DashedCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ══════════ PROJECTS ══════════ */}
      <section id="projects" style={{ padding: isMobile ? "60px 6vw 80px" : "80px 8vw 100px", background: DARKER, borderTop: `1px solid ${GA(0.06)}` }}>
        <Reveal>
          <SLabel text="projects.filter(p => p.ships)" />
          <STitle>Selected <span style={{ color: G, fontStyle: "italic" }}>Work</span></STitle>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: "14px" }}>
          {PROJECTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <DashedCard accent={p.color} style={{ padding: "26px 22px", transition: "transform 0.3s", height: "100%" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; }}>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "7px", letterSpacing: "0.22em", color: p.color, textTransform: "uppercase", marginBottom: "6px", display: "flex", justifyContent: "space-between" }}>
                  <span>{p.cat}</span><span>{p.year}</span>
                </div>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "15px", fontWeight: "700", color: "#EDEDF0", marginBottom: "9px", lineHeight: "1.25" }}>{p.title}</div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", lineHeight: "1.75", color: GA(0.4), marginBottom: "14px" }}>{p.desc}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {p.tags.map(t => <span key={t} style={{ fontSize: "7px", padding: "2px 7px", border: `1px solid ${p.color}44`, color: p.color, fontFamily: "'Inter',sans-serif", letterSpacing: "0.05em" }}>{t}</span>)}
                </div>
              </DashedCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ══════════ SYSTEM ARCHITECTURE ══════════ */}
      <section id="architecture" style={{ padding: isMobile ? "60px 6vw 80px" : "80px 8vw 100px", background: DARK, borderTop: `1px solid ${GA(0.06)}` }}>
        <Reveal>
          <SLabel text="architecture.design()" />
          <STitle>System <span style={{ color: G, fontStyle: "italic" }}>Architecture</span></STitle>
          <p style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", lineHeight: "1.9", color: GA(0.4), maxWidth: "600px", marginBottom: "16px", marginTop: "-32px" }}>
            AI Agent pipeline built with n8n orchestration, MCP Server, LLM prompt engineering, RAG retrieval/ranking, PyTorch/TensorFlow evaluation harnesses, Three.js 3D visualisations, Python/Node.js/Java Spring Boot microservices, Kubernetes — hover nodes to explore.
          </p>
        </Reveal>
        <div style={{ height: isMobile ? "320px" : "520px", border: `1px solid ${GA(0.08)}`, background: DARKER, position: "relative" }}>
          <LazyMount height="100%"><ArchScene /></LazyMount>
          {/* Legend */}
          <div style={{ position: "absolute", bottom: "16px", left: "16px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
            {[["AI/LLM", "#8B7EC8"], ["Gateway/Infra", "#00A4E4"], ["Agents", "#ea4b71"], ["Data", "#B8A06E"], ["Services", "#7C9DBF"]].map(([l, c]) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: c }} />
                <span style={{ fontFamily: "'Inter',sans-serif", fontSize: "8px", color: GA(0.4), letterSpacing: "0.1em" }}>{l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack Grid */}
        <div style={{ marginTop: "32px", display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : isTablet ? "repeat(3,1fr)" : "repeat(5,1fr)", gap: "10px" }}>
          {[["n8n", "Workflow AI", "#ea4b71"], ["MCP Server", "Model Context", "#FF6B35"], ["PyTorch / TensorFlow", "Model Evaluation", "#00A4E4"], ["RAG / Vector", "Knowledge Base", "#6BAE8E"], ["Kubernetes", "Orchestration", "#326CE5"], ["Three.js / WebGL", "3D Development", "#C8A96E"], ["Node.js", "Backend Runtime", "#68A063"], ["Python", "AI & Backend", "#3776AB"], ["Java / Spring Boot", "Microservices", "#F89820"], ["AR / XR", "Spatial Layer", "#8B7EC8"]].map(([t, s, c]) => (
            <Reveal key={t} delay={0.05}>
              <DashedCard accent={c} style={{ padding: "18px 14px", textAlign: "center", transition: "transform 0.3s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; }}>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "13px", fontWeight: "700", color: "#EDEDF0", marginBottom: "4px" }}>{t}</div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "7px", letterSpacing: "0.14em", textTransform: "uppercase", color: c }}>{s}</div>
              </DashedCard>
            </Reveal>
          ))}
        </div>

        {/* Full-stack architecture patterns — real production combinations, not just buzzwords */}
        <div style={{ marginTop: "50px" }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: GA(0.45), marginBottom: "16px" }}>Full-stack combinations shipped to production</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: "14px" }}>
            {[
              { stack: "MERN", layers: ["MongoDB", "Express.js", "React.js", "Node.js"], color: "#6BAE8E", proof: "KYC onboarding, Process Management System" },
              { stack: "PERN", layers: ["PostgreSQL", "Express.js", "React.js", "Node.js"], color: "#00A4E4", proof: "POS system, e-bidding platform" },
              { stack: "MEAN", layers: ["MongoDB", "Express.js", "Angular", "Node.js"], color: "#ea4b71", proof: "Enterprise SaaS, Angular component platforms" },
            ].map((s) => (
              <Reveal key={s.stack} delay={0.05}>
                <DashedCard accent={s.color} style={{ padding: "22px 24px", height: "100%" }}>
                  <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "24px", fontWeight: "700", color: s.color, marginBottom: "12px", letterSpacing: "0.02em" }}>{s.stack}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "14px" }}>
                    {s.layers.map(l => (
                      <div key={l} style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", color: GA(0.58), display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: s.color, flexShrink: 0 }} />{l}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", lineHeight: "1.6", color: GA(0.4), borderTop: `1px solid ${GA(0.08)}`, paddingTop: "10px" }}>{s.proof}</div>
                </DashedCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ EXPERIENCE ══════════ */}
      <section id="experience" style={{ padding: isMobile ? "60px 6vw 80px" : "80px 8vw 100px", background: DARKER, borderTop: `1px solid ${GA(0.06)}` }}>
        <Reveal>
          <SLabel text="experience.reduce(skills)" />
          <STitle>Career <span style={{ color: G, fontStyle: "italic" }}>Journey</span></STitle>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.85fr 1.15fr", gap: isMobile ? "36px" : "20px", alignItems: "start" }}>
          {!isMobile && <div style={{ position: "sticky", top: "110px" }}><LazyMount height="560px"><HelixScene /></LazyMount></div>}
          <div>
            {COMPANIES.map((c, i) => {
              const year = c.period.match(/\d{4}/)?.[0] ?? "";
              return (
                <Reveal key={c.id} delay={i * 0.07}>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "56px 1fr" : "104px 1fr", gap: isMobile ? "14px" : "28px", position: "relative", paddingBottom: i === COMPANIES.length - 1 ? "0" : "38px" }}>
                    <div style={{ position: "absolute", left: isMobile ? "24px" : "46px", top: "8px", bottom: 0, width: "1px", background: GA(0.12) }} />
                    <div style={{ position: "absolute", left: isMobile ? "20px" : "42px", top: "3px", width: "9px", height: "9px", borderRadius: "50%", background: c.color, boxShadow: `0 0 12px ${c.color}` }} />
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: isMobile ? "24px" : "36px", fontWeight: "700", color: GA(0.28), lineHeight: "1" }}>{year}</div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "4px" }}>
                        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "18px", fontWeight: "700", color: "#EDEDF0" }}>{c.role}</div>
                        {c.current && <span style={{ fontFamily: "'Inter',sans-serif", fontSize: "7px", letterSpacing: "0.14em", padding: "2px 7px", background: `${c.color}22`, color: c.color, border: `1px solid ${c.color}44` }}>CURRENT</span>}
                      </div>
                      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", color: c.color, letterSpacing: "0.03em", marginBottom: "3px" }}>{c.name} · {c.location}</div>
                      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", color: GA(0.35), letterSpacing: "0.08em", marginBottom: "10px" }}>{c.period}</div>
                      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "12px", lineHeight: "1.8", color: GA(0.5), maxWidth: "560px" }}>
                        <span style={{ color: c.color }}>{c.project}</span> — {c.desc}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
            <Reveal delay={0.45}>
              <DashedCard style={{ padding: "20px 26px", marginTop: "8px" }}>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.2em", textTransform: "uppercase", color: G, marginBottom: "6px" }}>Education</div>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "16px", fontWeight: "700", color: "#EDEDF0" }}>Master of Computer Applications</div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "11px", color: GA(0.45), marginTop: "4px" }}>Amity University, Noida · 2021–2023 · GPA 7.8</div>
              </DashedCard>
            </Reveal>
          </div>
        </div>
      </section>

      <VisitingCard isMobile={isMobile} />

      {/* ══════════ CONTACT ══════════ */}
      <section id="contact" style={{ padding: isMobile ? "70px 6vw 60px" : "100px 8vw 90px", background: `linear-gradient(135deg,${GA(0.04)} 0%,transparent 50%,rgba(0,164,228,0.03) 100%)`, borderTop: `1px solid ${GA(0.07)}`, textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "580px", height: "580px", borderRadius: "50%", background: `radial-gradient(circle,${GA(0.055)} 0%,transparent 70%)`, pointerEvents: "none" }} />
        <Reveal>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", color: G, marginBottom: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
            <div style={{ width: "36px", height: "1px", background: G }} />let's.connect()<div style={{ width: "36px", height: "1px", background: G }} />
          </div>
          <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(38px,5.5vw,72px)", fontWeight: "900", color: "#F5F5F7", lineHeight: "1.0", marginBottom: "20px" }}>
            Built to solve<br /><span style={{ color: G, fontStyle: "italic" }}>complex</span> problems
          </h2>
          <p style={{ fontFamily: "'Inter',sans-serif", fontSize: "12px", lineHeight: "2", color: GA(0.45), maxWidth: "420px", margin: "0 auto 44px" }}>
            Seeking challenging roles in AI engineering, full-stack architecture, and 3D/immersive experiences at ambitious companies.
          </p>
          <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", marginBottom: "44px" }}>
            <a href="mailto:devporwal00@gmail.com" style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", padding: "15px 36px", background: G, color: DARK, textDecoration: "none", fontWeight: "700", cursor: "none", transition: "all 0.3s", display: "inline-block" }}
              onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = `0 14px 36px ${GA(0.38)}`; }}
              onMouseLeave={e => { e.target.style.transform = ""; e.target.style.boxShadow = ""; }}>
              devporwal00@gmail.com
            </a>
            <a href="tel:+919582953281" style={{ fontFamily: "'Inter',sans-serif", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", padding: "15px 36px", background: "transparent", color: G, border: `1px solid ${GA(0.3)}`, textDecoration: "none", cursor: "none", transition: "all 0.3s", display: "inline-block" }}
              onMouseEnter={e => { e.target.style.background = GA(0.08); e.target.style.borderColor = G; }}
              onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.borderColor = GA(0.3); }}>
              +91 9582953281
            </a>
          </div>
          <div style={{ display: "flex", gap: "32px", justifyContent: "center" }}>
            {[["LinkedIn", "https://www.linkedin.com/in/-devgupta"], ["GitHub", "https://github.com/Devgupta0"], ["Email", "mailto:devporwal00@gmail.com"]].map(([l, h]) => (
              <a key={l} href={h} target="_blank" rel="noreferrer" style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.18em", textTransform: "uppercase", color: GA(0.35), borderBottom: `1px solid ${GA(0.12)}`, paddingBottom: "2px", textDecoration: "none", cursor: "none", transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = G}
                onMouseLeave={e => e.target.style.color = GA(0.35)}>
                {l}
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: isMobile ? "24px 6vw" : "20px 8vw", display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "8px" : "0", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${GA(0.06)}`, background: DARKER, textAlign: isMobile ? "center" : "left" }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.12em", color: GA(0.2) }}>© 2026 Dev Gupta · All rights reserved</div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.12em", color: GA(0.2) }}>React + Three.js · WebGL · GLSL</div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "9px", letterSpacing: "0.12em", color: GA(0.2) }}>Noida, India</div>
      </footer>

      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(26px)}to{opacity:1;transform:translateY(0)}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes scrollAnim{0%{transform:scaleY(0);transform-origin:top}50%{transform:scaleY(1);transform-origin:top}50.01%{transform:scaleY(1);transform-origin:bottom}100%{transform:scaleY(0);transform-origin:bottom}}
        *{box-sizing:border-box;margin:0;padding:0}
        html,body{background:#050607 !important;min-height:100%}
        @media(min-width:769px){html,body{cursor:none}}
        #root,#app{background:#050607}
        html{scroll-behavior:smooth}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-track{background:#050607}
        ::-webkit-scrollbar-thumb{background:rgba(200,169,110,0.28);border-radius:2px}
        ::selection{background:rgba(200,169,110,0.22);color:#F5F5F7}
        canvas{display:block !important}
        @media(max-width:768px){
          h1{font-size:clamp(52px,14vw,80px) !important}
          h2{font-size:clamp(28px,7vw,42px) !important;margin-bottom:32px !important}
          button,a{cursor:pointer !important}
        }
      `}</style>
    </div>
  );
}
